import { useEffect } from "react";
import { useForm, useFieldArray } from "react-hook-form";
import { CirclePlus } from "lucide-react";

import CustomInput from "../../../components/UI/PageInput/CustomInput";
import CenteredHeading from "../../../components/UI/headingsUI/CenteredHeading";
import GrayButton from "../../../components/UI/buttonUI/GrayButton";
import PrimaryButton from "../../../components/UI/buttonUI/PrimaryButton";
import { noSpaceOnlyValidation } from "../../../validations/formValidations";
import CustomFileInput from "../../../components/UI/PageInput/CustomFileInput";
import SelectedImagesBox from "../../../components/expense/add/SelectedImagesBox";
import { getCompressImage } from "../../../functions/getCompressImage";
import MultiCheckDropdown from "../../../components/UI/PageInput/MultiCheckDropdown";

const AddVehicleReadingForm = ({ loading, sendData, setupData }) => {
  const today = new Date().toISOString().split("T")[0];
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    control,
    getValues,
    formState: { errors },
    reset,
  } = useForm({
    defaultValues: {
      station_ids: [],
      log_date: today,
      morning_file: null,
      evening_file: null,
    },
  });

  const {
    morning_file,
    morning_photo,
    evening_file,
    evening_photo,
    station_ids,
  } = watch();

  const { append: appendMorningPhoto, remove: removeMorningPhoto } =
    useFieldArray({
      control,
      name: "morning_photo",
    });

  const { append: appendEveningPhoto, remove: removeEveningPhoto } =
    useFieldArray({
      control,
      name: "evening_photo",
    });

  // compress Morning Photo
  useEffect(() => {
    if (morning_file?.length) {
      removeMorningPhoto();
      getCompressImage(morning_file, appendMorningPhoto);
    }
  }, [appendMorningPhoto, morning_file, removeMorningPhoto]);

  // compress Evening Photo
  useEffect(() => {
    if (evening_file?.length) {
      removeEveningPhoto();
      getCompressImage(evening_file, appendEveningPhoto);
    }
  }, [appendEveningPhoto, evening_file, removeEveningPhoto]);

  // api call on submit
  const onSubmit = (data) => {
    const { morning_file, evening_file, ...rest } = data;
    const finalData = {
      ...rest,
      morning_reading_at: `${data.morning_reading_at}:00`,
      evening_reading_at: `${data.evening_reading_at}:00`,
    };
    sendData(finalData, reset);
  };

  return (
    <div className="form-container">
      <CenteredHeading title="Add Vehicle Reading" />
      <form
        className="form-border"
        onSubmit={!loading ? (e) => handleSubmit(onSubmit)(e) : undefined}
      >
        <div className="md:flex gap-2">
          <div className="w-full">
            <CustomInput
              type="text"
              name="vehicle_number"
              labelText="Vehicle Number *"
              placeholder="Enter"
              register={{
                ...register("vehicle_number", {
                  ...noSpaceOnlyValidation,
                  required: "Please enter vehicle number",
                }),
              }}
              error={errors.vehicle_number}
            />
          </div>
          <div className="w-full">
            <MultiCheckDropdown
              name="station_ids"
              label="Station *"
              keyName="name"
              keyId="id"
              showOptionId={true}
              options={setupData}
              error={errors.station_ids}
              selectedOptions={station_ids}
              control={control}
            />
          </div>
        </div>
        <div className="md:flex md:pt-2 gap-2">
          <div className="w-full">
            <CustomInput
              type="number"
              name="morning_reading"
              labelText="Morning Reading"
              placeholder="Enter"
              register={{
                ...register("morning_reading", {
                  required: "Please enter a morning reading",
                  validate: (value) => {
                    if (value.toString().length > 8) {
                      return "Invalid vehicle reading";
                    }
                    return true;
                  },
                }),
              }}
              error={errors.morning_reading}
            />
          </div>
          <div className="w-full">
            <CustomInput
              type="number"
              name="evening_reading"
              labelText="Evening Reading"
              placeholder="Enter"
              register={{
                ...register("evening_reading", {
                  required: "Please enter evening reading",
                  validate: (value) => {
                    if (value.toString().length > 8) {
                      return "Invalid vehicle reading";
                    }
                    const morningReading = Number(getValues("morning_reading"));
                    const eveningReading = Number(value);

                    if (eveningReading < morningReading) {
                      return "Evening reading cannot be less than morning reading";
                    }

                    return true;
                  },
                }),
              }}
              error={errors.evening_reading}
            />
          </div>
        </div>
        <div className="md:flex md:pt-2 gap-2">
          <div className="w-full">
            <CustomInput
              type="time"
              name="morning_reading_at"
              labelText="Morning Reading Time"
              register={{
                ...register("morning_reading_at", {
                  required: "Please select morning reading time",
                }),
              }}
              error={errors.morning_reading_at}
            />
          </div>
          <div className="w-full">
            <CustomInput
              type="time"
              name="evening_reading_at"
              labelText="Evening Reading Time"
              register={{
                ...register("evening_reading_at", {
                  required: "Please select evening reading time",

                  validate: (value) => {
                    const morningTime = getValues("morning_reading_at");

                    if (morningTime && value <= morningTime) {
                      return "Evening reading time must be after morning reading time";
                    }

                    return true;
                  },
                }),
              }}
              error={errors.evening_reading_at}
            />
          </div>
        </div>
        <div className="md:flex md:pt-2 gap-2">
          <div className="w-full">
            <CustomFileInput
              name="morning_file"
              placeholder="Add Morning Image* (jpg, png)"
              accept=".png, .jpg, image/png, image/jpeg"
              register={{
                ...register("morning_file", {
                  required: "Please choose a morning photo",
                }),
              }}
              multiple={false}
              error={errors.morning_file}
            />
            <SelectedImagesBox
              errors={errors.morning_file}
              selectedImages={morning_photo}
              setValue={setValue}
              name="morning_file"
              remove={removeMorningPhoto}
            />
          </div>
          <div className="w-full">
            <CustomFileInput
              name="evening_file"
              placeholder="Add Evening Image* (jpg, png)"
              accept=".png, .jpg, image/png, image/jpeg"
              register={{
                ...register("evening_file", {
                  required: "Please choose a Evening photo",
                }),
              }}
              multiple={false}
              error={errors.evening_file}
            />
            <SelectedImagesBox
              errors={errors.evening_file}
              selectedImages={evening_photo}
              setValue={setValue}
              name="evening_file"
              remove={removeEveningPhoto}
            />
          </div>
        </div>
        <p className="max-md:text-xs text-blue-500 mt-1">
          Date is automatically set to today and cannot be changed.
        </p>
        <div className="submitWrap">
          <p className="pr-2">
            <PrimaryButton
              btnText={!loading ? "Add Reading" : "Adding..."}
              loading={loading}
              Icon={CirclePlus}
              size={20}
            />
          </p>
          <p className="pl-2">
            <GrayButton btnText="Clear" width="w-40" onClick={() => reset()} />
          </p>
        </div>
      </form>
    </div>
  );
};
export default AddVehicleReadingForm;
